import {
  ProductAttributeCreateRequest,
  ProductAttributeUpdateRequest,
  ProductAttributeValueCreateRequest,
  ProductAttributeValueUpdateRequest,
  ProductAttributeSortOption
} from "@/api/types";
import type { ProductAttribute, AttributeValue as ProductAttributeValue } from "@/types/product-type";
import { apiClient } from '@/api/api-client';
import { ApiResponse, PageContent } from "@/types/api-response";

// Create a new product attribute
export async function createProductAttribute(
  request: ProductAttributeCreateRequest
): Promise<ApiResponse<ProductAttribute>> {
  try {
    const response = await apiClient.post('/product-attributes', request);
    console.log('Product attribute created:', response.data);
    return response.data as ApiResponse<ProductAttribute>;
  } catch (error) {
    console.error('Error creating product attribute:', error);
    throw error;
  }
}

// Get a single product attribute by ID
export async function getProductAttributeById(id: number): Promise<ApiResponse<ProductAttribute>> {
  try {
    const response = await apiClient.get(`/product-attributes/${id}`);
    console.log('Product attribute fetched:', response.data);
    return response.data as ApiResponse<ProductAttribute>;
  } catch (error) {
    console.error('Error fetching product attribute:', error);
    throw error;
  }
}

// Get all product attributes for a specific product
export async function getProductAttributesByProductId(
  productId: string | number,
  page: number = 0,
  size: number = 20,
  sort: ProductAttributeSortOption = 'id,asc'
): Promise<ApiResponse<PageContent<ProductAttribute>>> {
  try {
    const id = typeof productId === 'string' ? productId : productId.toString();
    const response = await apiClient.get(`/products/${id}/attributes`, {
      params: { page, size, sort }
    });
    console.log('Product attributes fetched:', response.data);
    return response.data as ApiResponse<PageContent<ProductAttribute>>;
  } catch (error) {
    console.error('Error fetching product attributes:', error);
    throw error;
  }
}

// Get all product attributes with pagination and sorting
export async function getAllProductAttributes(): Promise<ApiResponse<PageContent<ProductAttribute>>> {
  try {
    const response = await apiClient.get('/product-attributes');
    console.log('Product attributes fetched:', response.data);
    return response.data as ApiResponse<PageContent<ProductAttribute>>;
  } catch (error) {
    console.error('Error fetching product attributes:', error);
    throw error;
  }
}

// Search product attributes by term
export async function searchProductAttributes(
  searchTerm: string,
  page: number = 0,
  size: number = 20,
  sort: ProductAttributeSortOption = 'id,asc'
): Promise<ApiResponse<PageContent<ProductAttribute>>> {
  try {
    const response = await apiClient.get('/product-attributes/search', {
      params: { searchTerm, page, size, sort }
    });
    console.log('Product attributes search completed:', response.data);
    return response.data as ApiResponse<PageContent<ProductAttribute>>;
  } catch (error) {
    console.error('Error searching product attributes:', error);
    throw error;
  }
}

// Update an existing product attribute
export async function updateProductAttribute(
  id: string,
  request: ProductAttributeUpdateRequest
): Promise<ApiResponse<ProductAttribute>> {
  try {
    const response = await apiClient.put(`/product-attributes/${id}`, request);
    console.log('Product attribute updated:', response.data);
    return response.data as ApiResponse<ProductAttribute>;
  } catch (error) {
    console.error('Error updating product attribute:', error);
    throw error;
  }
}

// Delete a product attribute (soft delete)
export async function deleteProductAttribute(id: string): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.delete(`/product-attributes/${id}`);
    console.log('Product attribute deleted:', response.data);
    return response.data as ApiResponse<void>;
  } catch (error) {
    console.error('Error deleting product attribute:', error);
    throw error;
  }
}

// Restore a soft-deleted product attribute
export async function restoreProductAttribute(id: string): Promise<ApiResponse<{ productattributeId: string }>> {
  try {
    const response = await apiClient.post(`/product-attributes/${id}/restore`);
    console.log('Product attribute restored:', response.data);
    return response.data as ApiResponse<{ productattributeId: string }>;
  } catch (error) {
    console.error('Error restoring product attribute:', error);
    throw error;
  }
}

// Product Attribute Values API functions

// Create a new product attribute value
export async function createProductAttributeValue(
  request: ProductAttributeValueCreateRequest
): Promise<ApiResponse<ProductAttributeValue>> {
  try {
    const response = await apiClient.post('/attribute-values', request);
    console.log('Product attribute value created:', response.data);
    return response.data as ApiResponse<ProductAttributeValue>;
  } catch (error) {
    console.error('Error creating product attribute value:', error);
    throw error;
  }
}

// Get a single product attribute value by ID
export async function getProductAttributeValueById(id: number): Promise<ApiResponse<ProductAttributeValue>> {
  try {
    const response = await apiClient.get(`/attribute-values/${id}`);
    console.log('Product attribute value fetched:', response.data);
    return response.data as ApiResponse<ProductAttributeValue>;
  } catch (error) {
    console.error('Error fetching product attribute value:', error);
    throw error;
  }
}

// Get all product attribute values with pagination and sorting
export async function getAllProductAttributeValues(
  page: number = 0,
  size: number = 20,
  sort: string = 'id,asc'
): Promise<ApiResponse<PageContent<ProductAttributeValue>>> {
  try {
    const response = await apiClient.get('/attribute-values', {
      params: { page, size, sort }
    });
    console.log('Product attribute values fetched:', response.data);
    return response.data as ApiResponse<PageContent<ProductAttributeValue>>;
  } catch (error) {
    console.error('Error fetching product attribute values:', error);
    throw error;
  }
}

// Get product attribute values by attribute ID
export async function getProductAttributeValuesByAttributeId(
  attributeId: string,
  page: number = 0,
  size: number = 20,
  sort: string = 'sortOrder,asc'
): Promise<ApiResponse<PageContent<ProductAttributeValue>>> {
  try {
    const response = await apiClient.get(`/product-attributes/${attributeId}/values`, {
      params: { page, size, sort }
    });
    console.log('Product attribute values by attribute ID fetched:', response.data);
    return response.data as ApiResponse<PageContent<ProductAttributeValue>>;
  } catch (error) {
    console.error('Error fetching product attribute values by attribute ID:', error);
    throw error;
  }
}

// Update an existing product attribute value
export async function updateProductAttributeValue(
  id: string,
  request: ProductAttributeValueUpdateRequest
): Promise<ApiResponse<ProductAttributeValue>> {
  try {
    const response = await apiClient.put(`/attribute-values/${id}`, request);
    console.log('Product attribute value updated:', response.data);
    return response.data as ApiResponse<ProductAttributeValue>;
  } catch (error) {
    console.error('Error updating product attribute value:', error);
    throw error;
  }
}

// Delete a product attribute value (soft delete)
export async function deleteProductAttributeValue(id: string): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.delete(`/attribute-values/${id}`);
    console.log('Product attribute value deleted:', response.data);
    return response.data as ApiResponse<void>;
  } catch (error) {
    console.error('Error deleting product attribute value:', error);
    throw error;
  }
}

// Restore a soft-deleted product attribute value
export async function restoreProductAttributeValue(id: number): Promise<ApiResponse<{ productAttributeValueId: string }>> {
  try {
    const response = await apiClient.post(`/attribute-values/${id}/restore`);
    console.log('Product attribute value restored:', response.data);
    return response.data as ApiResponse<{ productAttributeValueId: string }>;
  } catch (error) {
    console.error('Error restoring product attribute value:', error);
    throw error;
  }
}

// Get all product attributes without pagination (for mass operations)
export async function getAllProductAttributesSimple(): Promise<ApiResponse<ProductAttribute[]>> {
  try {
    const response = await apiClient.get('/product-attributes', {
      params: { page: 0, size: 1000, sort: 'id,asc' }
    });
    console.log('All product attributes fetched:', response.data);
    return response.data as ApiResponse<ProductAttribute[]>;
  } catch (error) {
    console.error('Error fetching all product attributes:', error);
    throw error;
  }
}

// Get all product attribute values without pagination (for mass operations)
export async function getAllProductAttributeValuesSimple(): Promise<ApiResponse<ProductAttributeValue[]>> {
  try {
    const response = await apiClient.get('/attribute-values', {
      params: { page: 0, size: 1000, sort: 'id,asc' }
    });
    console.log('All product attribute values fetched:', response.data);
    return response.data as ApiResponse<ProductAttributeValue[]>;
  } catch (error) {
    console.error('Error fetching all product attribute values:', error);
    throw error;
  }
}
